import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { createEffect, ofType, Actions } from "@ngrx/effects";
import { from } from "rxjs";
import { exhaustMap, map, catchError, tap, mergeMap } from "rxjs/operators";

import * as ProductActionTypes from "../../actions/products/product.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { AppState } from "../../reducers";

import { ProductService } from "src/app/shared/services/products/products/product.service";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { SnackBarUtils } from "src/app/shared/utils/snackbarUtils";
import { CategoriesService } from "src/app/shared/services/categories/categories.service";
import { PropertiesService } from "src/app/shared/services/properties/properties.service";

@Injectable()
export class ProductsEffects {
  getProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getProductsList),
      exhaustMap((action) =>
        this.productService
          .getProducts(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ProductActionTypes.loadProductsList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ProductActionTypes.loadProductsList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Products.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getWarehouseProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getWarehouseProductsList),
      exhaustMap((action) =>
        this.productService
          .getWarehouseProductList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ProductActionTypes.loadWarehouseProductsList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ProductActionTypes.loadProductsList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Products.loadWarehouseProductsError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getExcelProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getProductsExcelList),
      exhaustMap((action) =>
        this.productService
          .getProducts(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              ProductActionTypes.loadProductsExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ProductActionTypes.loadProductsExcelList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Products.loadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Products Table Config
  getProductsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getProductsTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              ProductActionTypes.loadProductsTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                ProductActionTypes.loadProductsTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Products Table Config
  saveProductsTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.saveProductsTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            ProductActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              ProductActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.createProduct),
      exhaustMap((action) =>
        this.productService.createProduct(action).pipe(
          mergeMap((result) => [
            ProductActionTypes.resetCreation(),
            ProductActionTypes.getProduct({
              code: result?.result?.code,
            }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Products.CreateProduct",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ProductActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Products.errorCreateProduct",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.updateProduct),
      exhaustMap((action) =>
        this.productService.updateProduct(action).pipe(
          map((result) => {
            return ProductActionTypes.getProduct({
              code: result?.result?.code,
            });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Products.UpdateProduct",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error) => {
            const actions = [
              ProductActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Products.errorUpdateProduct",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getProduct),
      exhaustMap((action) =>
        this.productService.getProduct(action.code).pipe(
          map((result) => ProductActionTypes.loadProduct({ response: result })),
          catchError((error) => {
            const actions = [
              ProductActionTypes.loadProduct({ response: [] }),
              MessagesActionTypes.setSnackbar(
                SnackBarUtils.setSnackBar(
                  "Products.loadProductError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getFamilyProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getFamilyProductsList),
      exhaustMap((action) =>
        this.productService.getFamilyProducts(action.pageReq).pipe(
          map((result) =>
            ProductActionTypes.loadFamilyProductsList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ProductActionTypes.loadFamilyProductsList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Products.loadFamilyError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getGroupProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getGroupProductsList),
      exhaustMap((action) =>
        this.productService.getGroupProducts(action.pageReq).pipe(
          map((result) =>
            ProductActionTypes.loadGroupProductsList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ProductActionTypes.loadGroupProductsList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Products.loadGroupsError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getUnitProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getUnitProductsList),
      exhaustMap((action) =>
        this.productService.getUnitsProducts(action.pageReq).pipe(
          map((result) =>
            ProductActionTypes.loadUnitProductsList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ProductActionTypes.loadUnitProductsList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Products.loadProductUnitError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getProductTypes$ = createEffect(() => 
    this.actions$.pipe(
      ofType(ProductActionTypes.getProductTypesList),
      exhaustMap((action) =>
        this.productService.getProductTypes(action.pageReq).pipe(
          map((result) =>
            ProductActionTypes.loadProductTypesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ProductActionTypes.loadProductTypesList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Products.loadProductTypesError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Barcode Types
  getBarcodeTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getBarcodeTypes),
      exhaustMap((action) =>
        this.productService.getBarcodeTypes(action.pageReq).pipe(
          map((result) =>
            ProductActionTypes.loadBarcodeTypes({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ProductActionTypes.loadBarcodeTypes({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.loadBarcodeTypesError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getCategoriesByUsage),
      exhaustMap((action) =>
        this.categoriesService
          .getCategoriesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              ProductActionTypes.loadCategoriesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Products.ErrorLoadCategories"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getProperties$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getPropertiesByUsage),
      exhaustMap((action) =>
        this.propertiesService
          .getPropertiesByUsage(action.pageReq, action.usageCode)
          .pipe(
            map((result) =>
              ProductActionTypes.loadPropertiesByUsage({
                response: result,
              })
            ),
            catchError((error) => {
              const actions = [
                ProductActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  SnackBarUtils.setSnackBar(
                    this.translate.instant("Products.ErrorLoadProperties"),
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getProductSiteList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.getProductSiteList),
      exhaustMap((action) =>
        this.productService.getProductSiteList(action.code).pipe(
          map((result) =>
            ProductActionTypes.loadProductSiteList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              ProductActionTypes.loadProductSiteList({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Products.loadProductSiteListError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createSiteList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductActionTypes.createSiteList),
      exhaustMap((action) =>
        this.productService.createProductSiteList(action).pipe(
          map((result) => {
            return ProductActionTypes.getProductSiteList({ code: action.productCode });
          }),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Products.productSiteListSaved",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),
          catchError((error: any) => {
            const actions = [
              ProductActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Products.productSiteListSaveFailed",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );


  constructor(
    private actions$: Actions,
    private productService: ProductService,
    private translate: TranslateService,
    private tableConfigService: TableConfigService,
    private store$: Store<AppState>,
    private categoriesService: CategoriesService,
    private propertiesService: PropertiesService
  ) {}

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
